import { FC } from 'react'
import { Button, Loader, Table } from 'rsuite'
import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'

import useIsOnMobile from '../../../services/useIsOnMobile'
import { ScreenSize } from '../../../utils/constants'
import useUrls from '../../../services/useUrls'
import UserPopover from '../UserPopover'
import { OrderList } from '../../../utils/types/buyerCatalogs'
import { getCatalogLink, getPickedSuppliers } from '../../../lib/userCatalog'

import styles from '../../../styles/BuyerPortal/MyOrderLists.module.less'
import buttonStyles from '../../../styles/CustomButtons.module.less'

const { Column, HeaderCell, Cell } = Table

interface CatalogsTableProps {
  data: OrderList[]
  isDataLoading: boolean
}

const CatalogsTable: FC<CatalogsTableProps> = (props) => {
  const { data, isDataLoading } = props
  const { t } = useTranslation(('buyerProfile'))
  const { urlT } = useUrls()

  const isOnLgScreen = useIsOnMobile(ScreenSize.lg)
  const isOnTablet = useIsOnMobile(ScreenSize.md)

  return (
    <Table
      key={data.length}
      renderLoading={() => (isDataLoading ? (
        <Loader
          backdrop
          size="lg"
          center
        />
      ) : null)}
      rowHeight={120}
      className={`
          ${styles['order-list-table']}
          ${isOnTablet ? 'margin-left-spacer-double margin-top-spacer-quadruple' : ''}
          ${styles['list-table']}
        `}
      data={data}
      hover={false}
      autoHeight
      bordered
    >
      <Column
        flexGrow={10}
        verticalAlign="middle"
      >
        <HeaderCell>{t('Catalog name')}</HeaderCell>
        <Cell className={styles['list-name']}>
          {({ id, name }) => (
            <Link
              data-testid="catalogs-table-catalog-name-link"
              href={urlT(getCatalogLink(id, name))}
              shallow
            >
              {name}
            </Link>
          )}
        </Cell>
      </Column>

      <Column
        flexGrow={isOnLgScreen ? 8 : 10}
        verticalAlign="middle"
      >
        <HeaderCell>{t('Picked products')}</HeaderCell>
        <Cell>
          {(rowData) => (
            rowData.items.length
          )}
        </Cell>
      </Column>

      <Column
        flexGrow={12}
        verticalAlign="middle"
      >
        <HeaderCell>{t('Selected suppliers')}</HeaderCell>
        <Cell>
          {(rowData) => (
            <div className={styles['picked-suppliers-width']}>
              {getPickedSuppliers(rowData.items, t)}
            </div>
          )}
        </Cell>
      </Column>

      <Column
        flexGrow={6}
        verticalAlign="middle"
      >
        <HeaderCell>{' '}</HeaderCell>
        <Cell>
          {({ editor, owner, ownerStatus }) => {
            const actualOwner = editor || owner
            const titleText = t('Created by {{ owner }}', { owner: actualOwner })
            const userShortTitle = !editor && ownerStatus === 0 ? (
              <>
                {titleText}
                <br />
                <span>
                  {t('User has been disabled')}
                </span>
              </>
            ) : titleText

            return (
              <UserPopover
                fullName={actualOwner || ''}
                hover
                isAdmin={false}
                title={userShortTitle}
              />
            )
          }}
        </Cell>
      </Column>

      <Column
        flexGrow={12}
        verticalAlign="middle"
        align="center"
      >
        <HeaderCell>{' '}</HeaderCell>
        <Cell>
          {({ id, name }) => (
            <Link
              href={urlT(getCatalogLink(id, name))}
              shallow
            >
              <Button
                appearance="primary"
                className={`${buttonStyles['custom-button']} ${buttonStyles['button-primary']} ${buttonStyles['button-caret']}`}
              >
                {t('View')}
              </Button>
            </Link>
          )}
        </Cell>
      </Column>
    </Table>
  )
}

export default CatalogsTable
